import React from 'react';
import { useAuth } from '@/lib/AuthContext';
// import { base44 } from '@/api/base44Client'; // Removido - agora usa Supabase
import { supabase } from '@/lib/supabase'; // Adicionado
import { useQuery } from '@tanstack/react-query';
import { formatCurrency, PLANS } from '@/lib/planConfig';
import { TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  completed: { label: 'Completed', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
};

export default function ActiveInvestments() {
  const { user } = useAuth();

  const { data: investments = [], isLoading } = useQuery({
    queryKey: ['investments', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('investments')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  if (isLoading) return null;
  if (investments.length === 0) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-gold" />
          My Investments
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Track your active investments and history</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {investments.map((inv) => {
          const plan = PLANS[inv.plan];
          const status = STATUS_CONFIG[inv.status] || STATUS_CONFIG.pending;
          const capPercent = inv.earning_cap > 0
            ? Math.min(((inv.total_earned || 0) / inv.earning_cap) * 100, 100)
            : 0;

          return (
            <div key={inv.id} className="rounded-xl border border-border bg-card p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-foreground">{plan?.name || inv.plan}</p>
                  <p className="text-2xl font-bold gold-text mt-1">{formatCurrency(inv.amount)}</p>
                </div>
                <Badge className={`text-xs border ${status.color}`}>{status.label}</Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Daily Rate</p>
                  <p className="font-semibold text-gold">{(inv.current_daily_rate || 0).toFixed(2)}%</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Total Earned</p>
                  <p className="font-semibold text-green-400">{formatCurrency(inv.total_earned || 0)}</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Cap (300%)</p>
                  <p className="font-semibold text-foreground">{formatCurrency(inv.earning_cap || 0)}</p>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Days w/o withdrawal</p>
                  <p className="font-semibold text-foreground">{inv.days_without_withdrawal || 0}</p>
                </div>
              </div>

              {inv.earning_cap > 0 && (
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Cap progress</span>
                    <span>{capPercent.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-gold to-gold-hover"
                      style={{ width: `${capPercent}%` }}
                    />
                  </div>
                </div>
              )}

              {inv.activation_date && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Activated on {format(new Date(inv.activation_date), 'MM/dd/yyyy')}
                </p>
              )}
              {!inv.activation_date && inv.status === 'pending' && (
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Awaiting deposit confirmation
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}